import { useTenantMe } from "@/lib/tenant-me";
import { usePlanUsage, planDisplayName } from "@/lib/plan-usage";
import { SettingsSection } from "@/components/v2/account/SettingsSection";
import { cn } from "@/lib/utils";

export function WorkspaceBilling() {
  const me = useTenantMe();
  const usage = usePlanUsage();

  const tenant = me.data?.tenant;

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Plan"
        description="Everything is on the Pro plan — free while we build. No billing, no card, no catch."
      >
        <div className="px-5 py-4 flex items-center gap-3">
          <p className="text-2xl font-semibold tracking-tight">
            {planDisplayName(usage?.plan ?? "PRO")}
          </p>
          <span className="text-[11px] uppercase tracking-wider rounded-full bg-primary/10 text-primary px-2.5 py-0.5 font-medium">
            Free while we build
          </span>
        </div>
        {tenant?.createdAt && (
          <p className="px-5 pb-4 text-xs text-muted-foreground">
            Workspace active since {new Date(tenant.createdAt).toLocaleDateString()}
          </p>
        )}
      </SettingsSection>

      <SettingsSection
        title="Usage"
        description="Counts reset every Monday at 00:00 in your workspace timezone."
      >
        <div className="px-5 py-4 grid gap-4 md:grid-cols-2">
          <UsageRow
            label="Services"
            current={usage?.current.services ?? 0}
            cap={usage?.limits.maxServices ?? null}
          />
          <UsageRow
            label="Public bookings this week"
            current={usage?.current.publicBookingsThisWeek ?? 0}
            cap={usage?.limits.publicBookingsPerWeek ?? null}
          />
        </div>
      </SettingsSection>
    </div>
  );
}

function UsageRow({
  label,
  current,
  cap,
}: {
  label: string;
  current: number;
  cap: number | null;
}) {
  const pct = cap == null ? 0 : Math.min(100, Math.round((current / cap) * 100));
  const tone =
    cap == null
      ? "bg-trend-positive"
      : pct >= 100
      ? "bg-destructive"
      : pct >= 80
      ? "bg-amber-500"
      : "bg-primary";
  return (
    <div className="rounded-md border bg-card p-4">
      <div className="flex items-baseline justify-between">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-sm font-semibold tabular-nums">
          {current}
          {cap == null ? (
            <span className="text-muted-foreground"> · Unlimited</span>
          ) : (
            <span className="text-muted-foreground"> / {cap}</span>
          )}
        </p>
      </div>
      <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden">
        <div
          className={cn("h-full transition-all", tone)}
          style={{ width: cap == null ? "100%" : `${pct}%` }}
        />
      </div>
    </div>
  );
}
